import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Slide from "@mui/material/Slide";
import Snackbar from "@mui/material/Snackbar";
import { v4 as uuidv4 } from "uuid";

import { Alert, AlertItemMessage, AlertProps, AlertStatus } from "merchant-portal-common/Alert";

export interface OnCloseWithExpiration {
	callback: () => void;
	duration: number;
}

export interface ProviderAlertProps extends Omit<AlertProps, "onClose"> {
	id: string;
	onClose: OnCloseWithExpiration | (() => void);
}

export interface AlertContextValue {
	addAlert: (item: ProviderAlertProps) => void;
	dismissAlert: () => void;
}

export const StandardAlertTimeout = 10000;

export const AlertContext = React.createContext<AlertContextValue>({
	addAlert: () => {},
	dismissAlert: () => {},
});

export const useAlertContext = () => {
	const alertContext = useContext(AlertContext);
	if (!alertContext) {
		throw new Error("Missing AlertProvider");
	}
	return alertContext;
};

export const AlertProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [alert, setAlert] = useState<ProviderAlertProps | null>(null);
	const [isAlertRendered, setIsAlertRendered] = useState(false);
	const exitTransitionRef = useRef<NodeJS.Timeout | null>(null);

	const dismissAlert = useCallback(() => {
		setIsAlertRendered(false);
		exitTransitionRef.current = setTimeout(() => {
			setAlert(null);
		}, 250);
	}, []);

	const addAlert = useCallback(
		(incomingAlert: ProviderAlertProps) => {
			if (alert === null) {
				setAlert(incomingAlert);
				setIsAlertRendered(true);
			}
			if (alert) {
				setIsAlertRendered(false);
				exitTransitionRef.current = setTimeout(() => {
					setAlert(incomingAlert);
					setIsAlertRendered(true);
				}, 250);
			}
		},
		[alert],
	);

	useEffect(() => {
		return () => clearTimeout(exitTransitionRef.current || undefined);
	}, []);

	const alertContextValue = useMemo(
		() => ({
			addAlert,
			dismissAlert,
		}),
		[addAlert, dismissAlert],
	);

	return (
		<AlertContext.Provider value={alertContextValue}>
			{alert && (
				<Snackbar
					onClose={() => dismissAlert()}
					slotProps={{
						transition: Slide,
						clickAwayListener: { onClickAway: () => null },
					}}
					open={isAlertRendered}
					anchorOrigin={{ vertical: "top", horizontal: "right" }}
					autoHideDuration={
						// if onClose is an object, the alert has a duration and should expire automatically
						alert?.onClose && typeof alert?.onClose === "object"
							? alert?.onClose?.duration
							: undefined
					}
				>
					<Alert key={alert?.id} {...alert} onClose={() => dismissAlert()} />
				</Snackbar>
			)}
			{children}
		</AlertContext.Provider>
	);
};

interface UseAlertParameters {
	message: AlertItemMessage;
	status: AlertStatus;
	duration?: number;
}

export const useAlert = () => {
	const { addAlert, dismissAlert } = useAlertContext();

	const callback = useCallback(
		({ message, status, duration }: UseAlertParameters) => {
			const id = uuidv4();
			addAlert({
				id,
				message,
				status,
				onClose: duration
					? { callback: () => dismissAlert(), duration }
					: () => dismissAlert(),
			});
		},
		[addAlert, dismissAlert],
	);

	return callback;
};
